import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";

/**
 * PasswordInput
 *
 * Password field with a "reveal while typing" UX:
 *   - Starts masked (type="password").
 *   - While the user types, characters are briefly visible (type="text").
 *   - After `hideDelay` ms of keyboard inactivity, the field re-masks.
 *   - An eye-icon on the right cycles the reveal mode through three states:
 *       "auto"           — default reveal-while-typing behaviour
 *       "visible"        — pinned always visible
 *       "hidden"         — pinned always masked (typing does NOT reveal)
 *
 * Props: same as a native <input> — value, onChange, placeholder, required,
 *   minLength, className, autoComplete, name, data-testid, …
 *
 * Extra props:
 *   hideDelay (ms, default 1000) — inactivity delay before re-masking in auto mode
 *   showToggle (default true) — render the eye icon toggle
 */
const MODES = ["auto", "visible", "hidden"];

export default function PasswordInput({
  value,
  onChange,
  className = "",
  hideDelay = 1000,
  showToggle = true,
  "data-testid": testId,
  ...rest
}) {
  const [revealed, setRevealed] = useState(false); // transient reveal driven by keystrokes (auto mode)
  const [mode, setMode] = useState("auto");
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleChange = (e) => {
    onChange && onChange(e);
    if (mode !== "auto") return; // hidden or visible modes ignore keystroke-based reveal
    setRevealed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setRevealed(false), hideDelay);
  };

  const effectivelyVisible = mode === "visible" || (mode === "auto" && revealed);

  const cycleMode = () => {
    const nextIndex = (MODES.indexOf(mode) + 1) % MODES.length;
    setMode(MODES[nextIndex]);
    // Reset any pending transient reveal when leaving auto
    if (MODES[nextIndex] !== "auto" && timerRef.current) {
      clearTimeout(timerRef.current);
      setRevealed(false);
    }
  };

  const iconMap = {
    auto: Eye,
    visible: EyeOff,
    hidden: Lock,
  };
  const ToggleIcon = iconMap[mode];
  const ariaLabel = mode === "auto"
    ? "Reveal password while typing (currently auto)"
    : mode === "visible"
    ? "Password pinned visible — click to pin hidden"
    : "Password pinned hidden — click to switch back to auto";

  return (
    <div className="relative w-full" data-testid={testId ? `${testId}-wrap` : undefined}>
      <input
        {...rest}
        type={effectivelyVisible ? "text" : "password"}
        value={value ?? ""}
        onChange={handleChange}
        className={`${className} ${showToggle ? "pr-9" : ""}`}
        data-testid={testId}
      />
      {showToggle && (
        <button
          type="button"
          onClick={cycleMode}
          data-testid={testId ? `${testId}-eye` : "password-eye"}
          data-mode={mode}
          aria-label={ariaLabel}
          title={ariaLabel}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/45 hover:text-white transition-colors"
          tabIndex={-1}
        >
          <ToggleIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
